import { defModule, type TaskDeclaration, type TaskMessage, type UpdateResult } from '@st/redux'
import type { AuthError } from '@st/sdk'
import { match, P } from '@st/pattern'
import { observeCurrentUser } from '~/features/module-runtime'
import {
  disableTwoFactor,
  loadAccountSettings,
  saveProfileName,
  sendDisableTwoFactorCode,
  sendEnableTwoFactorCode,
  updateEmailNotifications,
  verifyEnableTwoFactorCode,
  type AccountSettingsOutcome
} from './st-account-settings-effects'

type PersistedSettingsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; emailNotificationsEnabled: boolean; saving: boolean }

type ProfileState = { status: 'idle' } | { status: 'saving'; name: string }

export type TwoFactorState =
  | { status: 'idle'; error?: AuthError }
  | { status: 'sendingEnableCode'; phoneNumber: string; currentPassword: string }
  | { status: 'enableCodeSent'; error?: AuthError }
  | { status: 'verifyingEnableCode'; code: string }
  | { status: 'enableSucceeded' }
  | { status: 'sendingDisableCode' }
  | { status: 'disableCodeSent'; error?: AuthError }
  | { status: 'disabling'; currentPassword: string; code: string }
  | { status: 'disableSucceeded' }
  | {
      status: 'finishing'
      expected:
        | 'enableTwoFactorCodeSent'
        | 'enableTwoFactorSucceeded'
        | 'disableTwoFactorCodeSent'
        | 'disableTwoFactorSucceeded'
    }

type AccountSettingsWorkflows = {
  settings: PersistedSettingsState
  profile: ProfileState
  twoFactor: TwoFactorState
}

type InactiveAccountSettingsState = {
  status: 'inactive'
  currentUserId: null
  settings: { status: 'idle' }
  profile: { status: 'idle' }
  twoFactor: { status: 'idle' }
}

type ActiveAccountSettingsState = AccountSettingsWorkflows & {
  status: 'active'
  currentUserId: string
}

type FinishingAccountSettingsState = AccountSettingsWorkflows & {
  status: 'finishing'
  currentUserId: string
  nextCurrentUserId: string | null
}

export type STAccountSettingsState =
  | InactiveAccountSettingsState
  | ActiveAccountSettingsState
  | FinishingAccountSettingsState

export type STAccountSettingsMessage =
  | TaskMessage<typeof observeCurrentUser>
  | TaskMessage<typeof loadAccountSettings>
  | AccountSettingsOutcome
  | { type: 'toggleEmailNotifications' }
  | { type: 'retrySettingsLoad' }
  | { type: 'saveProfileName'; name: string }
  | { type: 'resetTwoFactorFlow' }
  | { type: 'sendEnableTwoFactorCode'; phoneNumber: string; currentPassword: string }
  | { type: 'verifyEnableTwoFactorCode'; code: string }
  | { type: 'sendDisableTwoFactorCode' }
  | { type: 'disableTwoFactor'; currentPassword: string; code: string }

/**
 * Account settings is a product of three independent domain workflows:
 * persisted notification settings, profile-name saving, and two-factor setup.
 * It observes canonical Auth identity but never owns or synchronizes a copy of
 * the user. Auth-service mutations publish the updated user themselves.
 *
 * Identity and the initial settings read are state-owned Tasks. Mutations are
 * accepted Commands: once started, they settle even if Auth changes or the MFA
 * dialog closes. `finishing` reserves only those accepted outcomes; the chart
 * never copies request identities into state or messages.
 */
export const stAccountSettingsModule = defModule<{
  state: STAccountSettingsState
  message: STAccountSettingsMessage
}>({
  init: initialState,
  tasks: accountSettingsTasks,
  update: updateAccountSettings
})

function initialState(): STAccountSettingsState {
  return {
    status: 'inactive',
    currentUserId: null,
    settings: { status: 'idle' },
    profile: { status: 'idle' },
    twoFactor: { status: 'idle' }
  }
}

function accountSettingsTasks(state: STAccountSettingsState) {
  const tasks: TaskDeclaration<STAccountSettingsMessage>[] = [observeCurrentUser()]
  if (state.status == 'active' && state.settings.status == 'loading') {
    tasks.push(loadAccountSettings(state.currentUserId))
  }
  return tasks
}

function updateAccountSettings(
  state: STAccountSettingsState,
  message: STAccountSettingsMessage
): UpdateResult<STAccountSettingsState, STAccountSettingsMessage> {
  return match({ state, message })
    .returnType<UpdateResult<STAccountSettingsState, STAccountSettingsMessage>>()
    .with({ message: { type: 'currentUserChanged' } }, ({ state, message }) =>
      currentUserChanged(state, message.currentUserId)
    )
    .with(
      {
        state: { status: 'active', settings: { status: 'loading' } },
        message: { type: 'settingsLoaded' }
      },
      ({ state, message }) => ({
        ...state,
        settings: {
          status: 'loaded',
          emailNotificationsEnabled: message.emailNotificationsEnabled,
          saving: false
        }
      })
    )
    .with(
      {
        state: { status: 'active', settings: { status: 'loading' } },
        message: { type: 'settingsLoadFailed' }
      },
      ({ state }) => ({ ...state, settings: { status: 'failed' } })
    )
    .with(
      {
        state: { status: 'active', settings: { status: 'failed' } },
        message: { type: 'retrySettingsLoad' }
      },
      ({ state }) => ({ ...state, settings: { status: 'loading' } })
    )
    .with(
      {
        state: { status: 'active', settings: { status: 'loaded', saving: false } },
        message: { type: 'toggleEmailNotifications' }
      },
      ({ state }) => {
        const emailNotificationsEnabled = !state.settings.emailNotificationsEnabled
        return [
          {
            ...state,
            settings: { status: 'loaded', emailNotificationsEnabled, saving: true }
          },
          updateEmailNotifications(emailNotificationsEnabled)
        ]
      }
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          settings: { status: 'loaded', saving: true }
        },
        message: { type: 'settingsUpdated' }
      },
      ({ state }) => commandSettled({ ...state, settings: { ...state.settings, saving: false } })
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          settings: { status: 'loaded', saving: true }
        },
        message: { type: 'settingsUpdateFailed' }
      },
      ({ state }) =>
        commandSettled({
          ...state,
          settings: {
            ...state.settings,
            emailNotificationsEnabled: !state.settings.emailNotificationsEnabled,
            saving: false
          }
        })
    )
    .with(
      {
        state: { status: 'active', profile: { status: 'idle' } },
        message: { type: 'saveProfileName' }
      },
      ({ state, message }) => [
        { ...state, profile: { status: 'saving', name: message.name } },
        saveProfileName(state.currentUserId, message.name)
      ]
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          profile: { status: 'saving' }
        },
        message: { type: P.union('profileNameSaved', 'profileNameSaveFailed') }
      },
      ({ state }) => commandSettled({ ...state, profile: { status: 'idle' } })
    )
    .with(
      {
        state: { status: 'active', twoFactor: { status: 'idle' } },
        message: { type: 'sendEnableTwoFactorCode' }
      },
      ({ state, message }) => [
        {
          ...state,
          twoFactor: {
            status: 'sendingEnableCode',
            phoneNumber: message.phoneNumber,
            currentPassword: message.currentPassword
          }
        },
        sendEnableTwoFactorCode(message.phoneNumber, message.currentPassword)
      ]
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'sendingEnableCode' }
        },
        message: { type: 'enableTwoFactorCodeSent' }
      },
      ({ state }) => commandSettled({ ...state, twoFactor: { status: 'enableCodeSent' } })
    )
    .with(
      {
        state: { status: 'active', twoFactor: { status: 'enableCodeSent' } },
        message: { type: 'verifyEnableTwoFactorCode' }
      },
      ({ state, message }) => [
        { ...state, twoFactor: { status: 'verifyingEnableCode', code: message.code } },
        verifyEnableTwoFactorCode(message.code)
      ]
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'verifyingEnableCode' }
        },
        message: { type: 'enableTwoFactorSucceeded' }
      },
      ({ state }) => commandSettled({ ...state, twoFactor: { status: 'enableSucceeded' } })
    )
    .with(
      {
        state: { status: 'active', twoFactor: { status: 'idle' } },
        message: { type: 'sendDisableTwoFactorCode' }
      },
      ({ state }) => [
        { ...state, twoFactor: { status: 'sendingDisableCode' } },
        sendDisableTwoFactorCode()
      ]
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'sendingDisableCode' }
        },
        message: { type: 'disableTwoFactorCodeSent' }
      },
      ({ state }) => commandSettled({ ...state, twoFactor: { status: 'disableCodeSent' } })
    )
    .with(
      {
        state: { status: 'active', twoFactor: { status: 'disableCodeSent' } },
        message: { type: 'disableTwoFactor' }
      },
      ({ state, message }) => [
        {
          ...state,
          twoFactor: {
            status: 'disabling',
            currentPassword: message.currentPassword,
            code: message.code
          }
        },
        disableTwoFactor(message.currentPassword, message.code)
      ]
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'disabling' }
        },
        message: { type: 'disableTwoFactorSucceeded' }
      },
      ({ state }) => commandSettled({ ...state, twoFactor: { status: 'disableSucceeded' } })
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'finishing' }
        },
        message: { type: 'twoFactorFailed' }
      },
      ({ state }) => commandSettled({ ...state, twoFactor: { status: 'idle' } })
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: { status: 'finishing' }
        },
        message: {
          type: P.union(
            'enableTwoFactorCodeSent',
            'enableTwoFactorSucceeded',
            'disableTwoFactorCodeSent',
            'disableTwoFactorSucceeded'
          )
        }
      },
      ({ state, message }) => {
        if (message.type != state.twoFactor.expected) return unexpectedMessage(state, message)
        return commandSettled({ ...state, twoFactor: { status: 'idle' } })
      }
    )
    .with(
      {
        state: {
          status: P.union('active', 'finishing'),
          twoFactor: {
            status: P.union(
              'sendingEnableCode',
              'verifyingEnableCode',
              'sendingDisableCode',
              'disabling'
            )
          }
        },
        message: { type: 'twoFactorFailed' }
      },
      ({ state, message }) => commandSettled(twoFactorFailed(state, message))
    )
    .with(
      { state: { status: 'active' }, message: { type: 'resetTwoFactorFlow' } },
      ({ state }) => ({
        ...state,
        twoFactor: twoFactorCommandRunning(state.twoFactor)
          ? finishTwoFactorCommand(state.twoFactor)
          : { status: 'idle' }
      })
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function currentUserChanged(
  state: STAccountSettingsState,
  currentUserId: string | null
): STAccountSettingsState {
  if (state.status == 'finishing') {
    return { ...state, nextCurrentUserId: currentUserId }
  }
  if (state.currentUserId == currentUserId) return state
  if (state.status == 'active' && accountCommandRunning(state)) {
    return { ...state, status: 'finishing', nextCurrentUserId: currentUserId }
  }
  return stateForUser(currentUserId)
}

function stateForUser(currentUserId: string | null): STAccountSettingsState {
  if (!currentUserId) return initialState()
  return {
    status: 'active',
    currentUserId,
    settings: { status: 'loading' },
    profile: { status: 'idle' },
    twoFactor: { status: 'idle' }
  }
}

type CommandOwningAccountSettingsState = ActiveAccountSettingsState | FinishingAccountSettingsState

function accountCommandRunning(state: CommandOwningAccountSettingsState): boolean {
  return (
    (state.settings.status == 'loaded' && state.settings.saving) ||
    state.profile.status == 'saving' ||
    twoFactorCommandRunning(state.twoFactor)
  )
}

function twoFactorCommandRunning(twoFactor: TwoFactorState): boolean {
  return (
    twoFactor.status == 'sendingEnableCode' ||
    twoFactor.status == 'verifyingEnableCode' ||
    twoFactor.status == 'sendingDisableCode' ||
    twoFactor.status == 'disabling' ||
    twoFactor.status == 'finishing'
  )
}

function finishTwoFactorCommand(twoFactor: TwoFactorState): TwoFactorState {
  switch (twoFactor.status) {
    case 'sendingEnableCode':
      return { status: 'finishing', expected: 'enableTwoFactorCodeSent' }
    case 'verifyingEnableCode':
      return { status: 'finishing', expected: 'enableTwoFactorSucceeded' }
    case 'sendingDisableCode':
      return { status: 'finishing', expected: 'disableTwoFactorCodeSent' }
    case 'disabling':
      return { status: 'finishing', expected: 'disableTwoFactorSucceeded' }
    case 'finishing':
      return twoFactor
    default:
      throw new Error(`Cannot finish inactive two-factor phase ${twoFactor.status}`)
  }
}

function commandSettled(state: CommandOwningAccountSettingsState): STAccountSettingsState {
  if (state.status == 'active' || accountCommandRunning(state)) return state
  return stateForUser(state.nextCurrentUserId)
}

function twoFactorFailed(
  state: CommandOwningAccountSettingsState & {
    twoFactor: Extract<
      TwoFactorState,
      {
        status: 'sendingEnableCode' | 'verifyingEnableCode' | 'sendingDisableCode' | 'disabling'
      }
    >
  },
  message: Extract<AccountSettingsOutcome, { type: 'twoFactorFailed' }>
): CommandOwningAccountSettingsState {
  const twoFactor = match(state.twoFactor)
    .returnType<TwoFactorState>()
    .with({ status: 'sendingEnableCode' }, () => ({ status: 'idle', error: message.error }))
    .with({ status: 'verifyingEnableCode' }, () => ({
      status: 'enableCodeSent',
      error: message.error
    }))
    .with({ status: 'sendingDisableCode' }, () => ({ status: 'idle', error: message.error }))
    .with({ status: 'disabling' }, () => ({
      status: 'disableCodeSent',
      error: message.error
    }))
    .exhaustive()

  return { ...state, twoFactor }
}

function unexpectedMessage(
  state: STAccountSettingsState,
  message: STAccountSettingsMessage
): never {
  throw new Error(
    `Unexpected account-settings message ${message.type} for user ${state.currentUserId ?? 'none'}`
  )
}
